import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import debounce from 'lodash/debounce';

const LocationAutocomplete = ({ 
  onLocationSelect, 
  placeholder = "Buscar ubicación...",
  initialValue = ""
}) => {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(!!initialValue);
  const wrapperRef = useRef(null);

  // Función de búsqueda
  const fetchSuggestions = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://backend-production-1786.up.railway.app/api/geocoding/search?query=${encodeURIComponent(searchQuery)}`
      );

      if (!response.ok) {
        throw new Error('Error al buscar la ubicación');
      }

      const data = await response.json();
      
      const formattedSuggestions = data
        .filter(item => item.address)
        .map(item => ({
          id: item.place_id,
          display: formatAddress(item.address),
          city: item.address.city || item.address.town || item.address.village || '',
          province: item.address.state || '',
          country: item.address.country || '',
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          formatted_address: formatAddress(item.address)
        }))
        .filter(item => 
          item.display !== ', , ' && 
          (item.city || item.province) && 
          item.country
        );

      setSuggestions(formattedSuggestions);
      setShowSuggestions(true);

      if (formattedSuggestions.length === 0) {
        setError('No se encontraron ubicaciones');
      }

    } catch (error) {
      console.error('Error al buscar ubicaciones:', error);
      setError('Error al buscar ubicaciones. Por favor, intente de nuevo.');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Crear versión debounced de la función de búsqueda
  const debouncedFetch = useRef(
    debounce((searchQuery) => {
      fetchSuggestions(searchQuery);
    }, 300)
  ).current;

  const formatAddress = (address) => {
    const components = [];

    if (address.city) components.push(address.city);
    else if (address.town) components.push(address.town);
    else if (address.village) components.push(address.village);
    
    if (address.state && !components.includes(address.state)) {
      components.push(address.state);
    }
    
    if (address.country && !components.includes(address.country)) {
      components.push(address.country);
    }

    return components.filter(Boolean).join(', ');
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelected(false);
    setError(null);

    if (value.trim()) {
      debouncedFetch(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.display);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelected(true);
    setError(null);
    onLocationSelect(suggestion);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  // Efecto para manejar el valor inicial
  useEffect(() => {
    if (initialValue) {
      setQuery(initialValue);
      setSelected(true);
    }
  }, [initialValue]);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          className={`w-full pl-10 pr-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
            ${error ? 'border-red-500' : selected ? 'border-green-500' : 'border-gray-200'}`}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-1 ml-2">{error}</p>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin className="text-gray-400 flex-shrink-0" size={16} />
                <div>
                  <p className="text-sm text-gray-800 font-medium truncate">
                    {suggestion.city || suggestion.province}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {suggestion.display}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;